import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Person,
  VpnKey,
  Schedule,
  Security,
  Diamond,
  ExitToApp,
  LockReset,
  History,
  Comment,
  ExpandMore,
  ExpandLess,
  ChatBubbleOutline,
  KeyboardArrowDown,
  YouTube,
  Close as CloseIcon,
  ShowChart,
  Favorite,
  AutoAwesome,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { getUserProfile, logout, checkAuthStatus, changePassword, getUserCommentHistory } from '../utils/api';
import ScrollingWelcomeText from './ScrollingWelcomeText';
import CommentDialog from './CommentDialog';
import { formatDate, formatRelativeTime, getVipDaysLeft, getRiskStatusInfo, utc2localday } from '../utils/constants';

const DashboardContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(3),
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

// 站長頻道與會員專屬貼文（用戶在貼文下留言網站用戶名，站長核對後手動開通 VIP）
const CHANNEL_URL = 'https://www.youtube.com/@EIA007';
const MEMBER_POST_URL = 'https://www.youtube.com/post/UgkxNYR0lsjfDvltonwd6T-gtCvfUrdnRayX';

// VIP 福利清單：只保留真實存在的內容，可隨時增刪
const VIP_PERKS = [
  {
    icon: <ShowChart />,
    title: '完整歷史圖表',
    desc: '解鎖 5 年以上到全部年限的歷史數據，看清長週期的大趨勢。',
  },
  {
    icon: <Favorite />,
    title: '守護獨立與無廣告',
    desc: '你的支持，讓這裡繼續保持非營利、零廣告，專心做好內容。',
  },
  {
    icon: <AutoAwesome />,
    title: '更多驚喜',
    desc: '新的 VIP 功能上線時，第一時間為你開啟。',
  },
];

export default function Dashboard() {
  const [userProfile, setUserProfile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [vipDialogOpen, setVipDialogOpen] = React.useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertSeverity, setAlertSeverity] = React.useState('info');
  const [copied, setCopied] = React.useState(false); // 用戶名複製狀態

  // 密码相关状态
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');

  // 历史记录相关状态 - 增加分页功能
  const [commentHistory, setCommentHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false); // 載入更多狀態
  const [pagination, setPagination] = React.useState({
    page: 0,
    limit: 10,
    total_count: 0,
    total_pages: 0,
    has_next: false
  }); // 分頁狀態

  const [openCommentDialog, setOpenCommentDialog] = React.useState(false);
  const [selectedTweet, setSelectedTweet] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  // 點擊評論圖標的處理函數
  const handleCommentClick = (tweet) => {
      setSelectedTweet(tweet);
      setOpenCommentDialog(true);
  };

  // 處理載入更多歷史記錄
  const handleLoadMore = async () => {
    if (!pagination.has_next || loadingMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = pagination.page + 1;
      const data = await getUserCommentHistory(nextPage, pagination.limit);
      
      // 將新數據添加到現有數據中
      setCommentHistory(prevHistory => [...prevHistory, ...data.items]);
      setPagination(data.pagination);
    } catch (error) {
      console.error('載入更多評論歷史失敗:', error);
      setAlertMessage(`載入更多資料失敗: ${error.message}`);
      setAlertSeverity('error');
    } finally {
      setLoadingMore(false);
    }
  };

  // 获取用户资料
  React.useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (!checkAuthStatus()) {
          logout();
          return;
        }

        const profile = await getUserProfile();
        setUserProfile(profile);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('獲取用戶資料失敗:', error);
        setAlertMessage('獲取用戶資料失敗');
        setAlertSeverity('error');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  // 获取用户评论历史
  React.useEffect(() => {
    const loadCommentHistory = async () => {
      // 确保用户已登录且有有效的认证状态
      if (!userProfile || !checkAuthStatus()) return;
      
      setHistoryLoading(true);
      try {
        const data = await getUserCommentHistory(0, 10);
        setCommentHistory(data.items);
        setPagination(data.pagination);
      } catch (error) {
        console.error('獲取評論歷史失敗:', error);
        setAlertMessage(`獲取評論歷史失敗: ${error.message}`);
        setAlertSeverity('error');
        // 设置空数组，避免界面崩溃
        setCommentHistory([]);
        setPagination({
          page: 0,
          limit: 10,
          total_count: 0,
          total_pages: 0,
          has_next: false
        });
      } finally {
        setHistoryLoading(false);
      }
    };

    // 延迟执行，确保认证状态稳定
    const timeoutId = setTimeout(loadCommentHistory, 100);
    return () => clearTimeout(timeoutId);
  }, [userProfile]);

  // 複製用戶名（供用戶貼到會員貼文留言）
  const handleCopyUsername = async () => {
    try {
      await navigator.clipboard.writeText(userProfile.username);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* 剪貼簿不可用時忽略，用戶可手動選取 */
    }
  };

  // 处理修改密码
  const handlePasswordUpdate = async () => {
    setPasswordError('');

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('請填寫所有欄位');
      return;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&,])[A-Za-z\d@$!%*?&,]{13,30}$/.test(newPassword)) {
      setPasswordError('新密碼必須包含字母、數字、特殊字符(@$!%*?&,)，長度13-30位');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('新密碼與確認密碼不符');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(oldPassword, newPassword);
      setAlertMessage('密碼修改成功');
      setAlertSeverity('success');
      setPasswordDialogOpen(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(error.message || '密碼修改失敗');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  if (loading) {
    return (
      <DashboardContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={50} />
        </Box>
      </DashboardContainer>
    );
  }

  if (!userProfile) {
    return (
      <DashboardContainer>
        <Alert severity="error">
          無法獲取用戶資料，請重新登入
        </Alert>
      </DashboardContainer>
    );
  }

  const riskInfo = getRiskStatusInfo(userProfile.risk_status);
  const vipDaysLeft = getVipDaysLeft(userProfile.vip_expires_at);

  // 獲取要顯示的歷史記錄（已經是分頁數據）
  const displayedHistory = commentHistory;
  const hasMore = pagination.has_next;

  return (
    <DashboardContainer>
      {/* 頂部導航 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography 
        variant="h4" 
        component="h1" 
        fontWeight="bold" 
        fontFamily="LXGW WenKai Mono TC"
        sx={{ color: "#C0C0C0" }} // 銀灰色
        >
          用戶儀表板
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<LockReset />}
            onClick={() => setPasswordDialogOpen(true)}
            color="secondary"
          >
            修改密碼
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExitToApp />}
            onClick={logout}
            color="inherit"
          >
            登出
          </Button>
        </Box>
      </Box>

      {/* 提醒信息 */}
      {alertMessage && (
        <Alert 
          severity={alertSeverity} 
          sx={{ mb: 3 }}
          onClose={() => setAlertMessage('')}
        >
          {alertMessage}
        </Alert>
      )}

      {/* 用户资料卡片 */}
      <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        <Box display="flex" alignItems="center" gap={3}>
          <ScrollingWelcomeText performance="optimized" />
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {userProfile.username}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                icon={<Diamond />}
                label={userProfile.is_vip ? `VIP會員 (${vipDaysLeft}天)` : '普通用戶'}
                color={userProfile.is_vip ? 'primary' : 'default'}
                variant={userProfile.is_vip ? 'filled' : 'outlined'}
              />
              <Chip
                icon={<Security />}
                label={riskInfo.text}
                color={riskInfo.color}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* 统计信息 */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Person color="primary" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    用戶ID
                  </Typography>
                  <Typography variant="h6">
                    {userProfile.username === 'admin' ? '#Classified' : `#${userProfile.id}`}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Schedule color="info" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    註冊時間
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(userProfile.created_at)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <VpnKey color="success" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    最後登錄
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(userProfile.last_login)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatsCard>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Diamond color={userProfile.is_vip ? 'primary' : 'disabled'} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    VIP狀態
                  </Typography>
                  <Typography variant="body2">
                    {userProfile.is_vip 
                      ? `${utc2localday(userProfile.vip_expires_at)} 到期`
                      : '未開通'
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* VIP服務 */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            VIP會員服務
          </Typography>
          <Typography color="textSecondary" paragraph>
            {userProfile.is_vip
              ? `感謝你的支持！你的 VIP 還有 ${vipDaysLeft} 天。`
              : '加入站長頻道會員，解鎖 5 年以上的完整歷史圖表，並支持這裡繼續無廣告運營。'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Diamond />}
            onClick={() => setVipDialogOpen(true)}
          >
            {userProfile.is_vip ? '延長VIP' : '開通VIP'}
          </Button>
        </CardContent>
      </Card>

      {/* 评论历史板块 - 修改為分頁顯示 */}
      <Box
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(12px)",
          borderRadius: "16px",
          boxShadow: "0 4px 30px rgba(0,0,0,0.1)",
          px: 3,
          py: 4,
          mb: 4,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <History color="primary" />
            <Typography variant="h6">
              新聞評論足跡
            </Typography>
            <Chip 
              label={`總計 ${pagination.total_count} 條紀錄`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <IconButton
            onClick={() => setShowHistory(!showHistory)}
            size="small"
          >
            {showHistory ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={showHistory}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : displayedHistory.length === 0 ? (
            <Box textAlign="center" py={4}>
              <ChatBubbleOutline sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography color="textSecondary">
                還沒有參與評論的新聞紀錄
              </Typography>
            </Box>
          ) : (
            <>
              <Grid
                container
                rowSpacing={2}
                sx={{ alignItems: "center" }}
              >
                {displayedHistory.map((item, index) => (
                  <Grid key={index} xs={12} sm={10} md={8}>
                    <Box
                      sx={{
                        mx: "auto"
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="lightgray" sx={{ lineHeight: '20px' }}>
                          最後評論: {formatRelativeTime(item.latest_comment_at)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            onClick={() => handleCommentClick(item)}
                            startIcon={<ChatBubbleOutline sx={{ fontSize: 18 }} />}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '999px',
                              color: 'primary.main',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              height: '16px',
                              '&:hover': {
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease-in-out',
                            }}
                          >
                            我的: {item.user_comment_count}
                          </Button>
                          <Button
                            startIcon={<Comment sx={{ fontSize: 18 }} />}
                            sx={{
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '999px',
                              color: 'text.secondary',
                              fontSize: '0.85rem',
                              height: '16px',
                            }}
                            disabled
                          >
                            總數: {item.comment_count || 0}
                          </Button>
                        </Box>
                      </Box>

                      {/* 新聞内容 */}
                      <Typography
                        gutterBottom
                        variant="h5"
                        color="gray"
                        sx={{ 
                          whiteSpace: "pre-line",
                          cursor: "pointer",
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                        // onDoubleClick={() => handleCommentClick(item)} // 雙擊開評論
                        onClick={() => window.open(`/news/${item.tweet_id}`, '_blank')}
                      >
                        {`《${item.agency}》`+formatDate(item.created_at).slice(0,11)+"報導："+item.tweet_text.replace(/[\r\n]+/g, " ") + "\n【中文正體】" + item.txt_tw}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* 載入更多按鈕 */}
              {hasMore && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="outlined"
                    startIcon={loadingMore ? <CircularProgress size={16} /> : <KeyboardArrowDown />}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    sx={{
                      borderRadius: '999px',
                      px: 3,
                      py: 1,
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {loadingMore ? '載入中...' : `載入更多 (還有 ${pagination.total_count - commentHistory.length} 條)`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Collapse>
      </Box>

      {/* VIP 對話框 */}
      <Dialog
        open={vipDialogOpen}
        onClose={() => setVipDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            m: 2,
            borderRadius: 4,
            overflow: 'hidden',
            backgroundImage: 'none',
            boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          },
        }}
      >
        {/* 頭部 */}
        <Box
          sx={{
            position: 'relative',
            px: 3,
            pt: 4.5,
            pb: 3.5,
            textAlign: 'center',
            color: '#fff',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #4338ca 100%)',
          }}
        >
          <IconButton
            aria-label="關閉"
            onClick={() => setVipDialogOpen(false)}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: 'rgba(255,255,255,0.8)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: '#78350f',
              background: 'linear-gradient(135deg, #fde68a, #f59e0b)',
              boxShadow: '0 0 0 8px rgba(251,191,36,0.18), 0 8px 24px rgba(245,158,11,0.45)',
            }}
          >
            <Diamond sx={{ fontSize: 32 }} />
          </Box>

          <Typography variant="h5" component="h2" fontWeight={800} sx={{ lineHeight: 1.35 }}>
            {userProfile.is_vip ? '謝謝你，一直陪著我們 💛' : '一起把獨立財經資訊守下去'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.25, opacity: 0.88, lineHeight: 1.8 }}>
            {userProfile.is_vip
              ? `你的 VIP 還有 ${vipDaysLeft} 天。因為有你，這裡才能一直乾淨、沒有廣告。想繼續同行，隨時可以到頻道續訂。`
              : '經濟簡訊網沒有廣告，也不靠流量賺錢。有你的支持，我們才能繼續專心把每一份數據、每一則新聞做好。'}
          </Typography>
        </Box>

        {/* 內容 */}
        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.75 }}>
            加入站長頻道會員，即可獲得本站 VIP 權限
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {VIP_PERKS.map((perk) => (
              <Box key={perk.title} sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    flex: '0 0 40px',
                    height: 40,
                    borderRadius: 2.5,
                    display: 'grid',
                    placeItems: 'center',
                    color: '#d97706',
                    bgcolor: alpha('#f59e0b', 0.14),
                  }}
                >
                  {perk.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {perk.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {perk.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* 已加入會員 → 到會員專屬貼文留言（VIP 用戶不顯示） */}
          {!userProfile.is_vip && (
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 3,
                bgcolor: alpha('#f59e0b', 0.08),
                border: '1px solid',
                borderColor: alpha('#f59e0b', 0.25),
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                已經加入會員了？兩步開通
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                ① 到會員專屬貼文下，留言你的網站用戶名：
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    wordBreak: 'break-all',
                  }}
                >
                  {userProfile.username}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={copied ? <Check /> : <ContentCopy />}
                  onClick={handleCopyUsername}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {copied ? '已複製' : '複製'}
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                ② 我看到留言後會盡快為你開通，回來重新整理本頁，就能看到 VIP 狀態。
              </Typography>

              <Button
                fullWidth
                size="small"
                variant="outlined"
                onClick={() => window.open(MEMBER_POST_URL, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none' }}
              >
                前往會員專屬貼文
              </Button>
            </Box>
          )}
        </DialogContent>

        {/* 按鈕區 */}
        <Box sx={{ px: 3, pt: 2.5, pb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<YouTube />}
            onClick={() => window.open(CHANNEL_URL, '_blank', 'noopener,noreferrer')}
            sx={{
              py: 1.4,
              borderRadius: 999,
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'none',
              color: '#451a03',
              background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
              boxShadow: '0 8px 20px rgba(245,158,11,0.4)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(90deg, #d97706, #f59e0b)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 26px rgba(245,158,11,0.5)',
              },
            }}
          >
            {userProfile.is_vip ? '前往站長頻道續訂' : '訪問站長頻道，加入會員'}
          </Button>

          <Button
            fullWidth
            color="inherit"
            onClick={() => setVipDialogOpen(false)}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            我再想想
          </Button>

          <Typography variant="caption" color="text.secondary" align="center" sx={{ lineHeight: 1.7 }}>
            會員的付款與取消都在 YouTube 上完成。
            <br />
            無論是否加入，你都是我們很珍惜的讀者。
          </Typography>
        </Box>
      </Dialog>

      {/* 修改密碼對話框 */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          修改密碼
        </DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="當前密碼"
            type="password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            disabled={passwordLoading}
          />
          <TextField
            fullWidth
            label="新密碼"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            helperText="密碼須由字母數字特殊字符@$!%*?&,構成且不小於13位"
            disabled={passwordLoading}
          />
          <TextField
            fullWidth
            label="確認新密碼"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={passwordLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={passwordLoading}>
            取消
          </Button>
          <Button 
            onClick={handlePasswordUpdate} 
            variant="contained"
            disabled={passwordLoading || !oldPassword || !newPassword || !confirmPassword}
            color="secondary"
          >
            {passwordLoading ? <CircularProgress size={20} /> : '確認修改'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 評論彈窗 */}
      {selectedTweet && (
          <CommentDialog
              open={openCommentDialog}
              onClose={() => setOpenCommentDialog(false)}
              tweet={selectedTweet}
              isAuthenticated={isAuthenticated}
          />
      )}
    </DashboardContainer>
  );
}